import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { ConditionalLink } from "components/conditionalLink";
import ToolTip from "components/design/toolTip";
import { INKLE_R_AND_D_PRICING } from "constants/base";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useCreateTaskMutation } from "store/apis/getTemplates";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import FilingInfoIcon from "static/images/FilingInfo.svg";

const FILE_WITH_INKLE = "WITH_INKLE";
const FILE_EXTERNAL = "EXTERNAL";
const FILE_WITH_PARTNER = "WITH_PARTNER";

const SHOW_FILE_WITH_PARTNER = true;

const LaunchWithInkleModal = ({
  isOpen,
  close,
  onLaunch,
  isLoading,
}: ModalProps & { onLaunch: () => void; isLoading: boolean }) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <StatusModal
        type="warning"
        title="Launch Form 6765 with Inkle?"
        showCloseButton={false}
      >
        <div className="t-flex t-flex-col t-gap-2 t-text-body-sm t-text-text-30">
          <span>
            A new Form 6765 filing will be created. Our team will guide you
            through the full R&amp;D qualification process to maximize your tax
            credit.
          </span>
          <span>
            We charge <span className="t-font-semibold">15%</span> of the
            R&amp;D credit we identify, and nothing if we don&apos;t find one.
          </span>
        </div>
      </StatusModal>
      <Modal.Footer>
        <div className="t-flex t-items-center t-justify-between">
          <ConditionalLink to={INKLE_R_AND_D_PRICING} target="_blank">
            <span>
              <Button customType="link_neutral" size="small">
                Learn more
              </Button>
            </span>
          </ConditionalLink>
          <div className="t-flex t-gap-2 t-items-center">
            <Modal.RawClose asChild>
              <Button disabled={isLoading}>Go back</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={onLaunch}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Launch filing
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal.Content>
  </Modal.Root>
);

const ConfirmExternalFilingModal = ({
  isOpen,
  close,
  onLaunch,
  isLoading,
}: ModalProps & { onLaunch: () => void; isLoading: boolean }) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <StatusModal
        type="warning"
        title="Confirm external filing?"
        showCloseButton={false}
      >
        <div className="t-text-body-sm t-text-text-30">
          We&apos;ll launch a Form 6765 filing on your behalf to keep everything
          linked to your 1120. You&apos;ll just need to upload the filed copy
          once you&apos;re done. This will not have any additional costs.
        </div>
      </StatusModal>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button disabled={isLoading}>Go back</Button>
        </Modal.RawClose>
        <Button
          customType="primary"
          onClick={onLaunch}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Launch filing
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

const ConfirmPartnerFilingModal = ({
  isOpen,
  close,
  onLaunch,
  isLoading,
}: ModalProps & { onLaunch: () => void; isLoading: boolean }) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <StatusModal
        type="warning"
        title="Confirm filing with our partner?"
        showCloseButton={false}
      >
        <div className="t-text-body-sm t-text-text-30">
          We&apos;ll launch a Form 6765 filing on your behalf to keep everything
          linked to your 1120. Your partner will handle the filing directly.
        </div>
      </StatusModal>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button disabled={isLoading}>Go back</Button>
        </Modal.RawClose>
        <Button
          customType="primary"
          onClick={onLaunch}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Launch filing
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

type SetFieldValue = (field: string, value: any) => void;

/**
 * Renders the "How will you file Form 6765?" radio group.
 * Mapped to field type "filing_choice" in formControl.jsx.
 * Writes the selected option to the "filing_choice" form field via setFieldValue.
 */
export const FilingChoiceField = ({
  season,
  entityId,
  setFieldValue,
  values,
  onRadioChange,
}: {
  season?: string | number;
  entityId: string;
  setFieldValue: SetFieldValue;
  values: Record<string, any>;
  onRadioChange?: (value: string) => void;
}) => {
  const externalModal = useModal();
  const partnerModal = useModal();
  const [createTask, { isLoading }] = useCreateTaskMutation();
  const { successToast, alertToast } = useToast();

  const selected = values["filing_choice"] || "";

  const handleValueChange = (value: string) => {
    setFieldValue("filing_choice", value);
    onRadioChange?.(value);
    if (value === FILE_EXTERNAL) {
      externalModal.open();
    }
    if (value === FILE_WITH_PARTNER) {
      partnerModal.open();
    }
  };

  const handleGoBack = () => {
    setFieldValue("filing_choice", "");
    onRadioChange?.("");
    externalModal.close();
    partnerModal.close();
  };

  const launchFiling = async (templateId?: string) => {
    try {
      await createTask({
        entity: entityId,
        metaData: {
          amount: "Variable",
          base_task_template_id: templateId,
          season: season ? String(season) : "",
          skip_payment_mode: "ZERO_ENTRY",
        } as any,
      }).unwrap();
      successToast({ message: "Form 6765 filing launched." });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleLaunchExternal = async () => {
    await launchFiling(process.env.PUBLIC_FORM_6765_EXTERNAL_TEMPLATE_ID);
    externalModal.close();
  };

  const handleLaunchPartner = async () => {
    await launchFiling(process.env.PUBLIC_FORM_6765_WITH_INKLE_TEMPLATE_ID);
    partnerModal.close();
  };

  return (
    <div className="t-flex t-flex-col t-gap-2">
      <div className="t-text-body-sm t-text-text-100">
        How will you file Form 6765?
        <ToolTip
          text="Used by businesses to claim tax credits for research and development expenses. It is filed along with Form 1120 to claim these credits, helping reduce tax liability (or payroll taxes for startups) and incentivize innovation."
          propClass="!t-text-left !t-max-w-72"
        >
          <img src={FilingInfoIcon} alt="info" className="t-ml-1" />
        </ToolTip>
      </div>
      <RadioGroup.Root value={selected} onValueChange={handleValueChange}>
        <RadioGroup.Content className="t-flex t-flex-col t-gap-2">
          {SHOW_FILE_WITH_PARTNER ? (
            <RadioGroup.Item
              value={FILE_WITH_PARTNER}
              className="t-text-body-sm t-text-text-60"
            >
              File with our partner
            </RadioGroup.Item>
          ) : (
            <RadioGroup.Item
              value={FILE_WITH_INKLE}
              className="t-text-body-sm t-text-text-60"
            >
              File with Inkle
            </RadioGroup.Item>
          )}
          <RadioGroup.Item
            value={FILE_EXTERNAL}
            className="t-text-body-sm t-text-text-60"
          >
            File externally and upload the filed copy once it&apos;s done
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>
      <div className="t-text-caption t-italic t-text-text-30">
        We will launch a Form 6765 filing in either case, so your Form 1120
        remains on track.
      </div>

      <ConfirmExternalFilingModal
        isOpen={externalModal.isOpen}
        close={handleGoBack}
        onLaunch={handleLaunchExternal}
        isLoading={isLoading}
      />
      <ConfirmPartnerFilingModal
        isOpen={partnerModal.isOpen}
        close={handleGoBack}
        onLaunch={handleLaunchPartner}
        isLoading={isLoading}
      />
    </div>
  );
};

/**
 * Renders the launch button after all 4 FPT questions are answered Yes.
 * JSON gating ensures this only renders when filing_choice=WITH_INKLE and all 4 FPT=Yes.
 * Mapped to field type "file_form_6765" in formControl.jsx.
 */
export const FileForm6765 = ({
  season,
  entityId,
}: {
  season?: string | number;
  entityId: string;
}) => {
  const withInkleModal = useModal();
  const [createTask, { isLoading }] = useCreateTaskMutation();
  const { successToast, alertToast } = useToast();

  const handleLaunch = async () => {
    try {
      const templateId = process.env.PUBLIC_FORM_6765_WITH_INKLE_TEMPLATE_ID;
      await createTask({
        entity: entityId,
        metaData: {
          amount: "Variable",
          base_task_template_id: templateId,
          season: season ? String(season) : "",
          skip_payment_mode: "ZERO_ENTRY",
        } as any,
      }).unwrap();
      successToast({ message: "Form 6765 filing launched." });
      withInkleModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div>
        <Button
          customType="primary"
          size="small"
          type="button"
          onClick={withInkleModal.open}
        >
          Launch Form 6765 with Inkle
        </Button>
      </div>
      <LaunchWithInkleModal
        isOpen={withInkleModal.isOpen}
        close={withInkleModal.close}
        onLaunch={handleLaunch}
        isLoading={isLoading}
      />
    </div>
  );
};
